/**
 * PlayerBar — 비디오 아래 독립 72px 카드 (Figma 585:506).
 * Play/Pause, ProgressBar, Time, Volume, Settings, Fullscreen 을 조합.
 */
import { useCallback, useEffect, useState, type RefObject } from 'react'
import { Maximize, Minimize, Pause, Play, Settings } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import type { QueturePlayerHandle } from '@quebon/shared-learning'
import type { PlayerState } from './usePlayerState'
import ProgressBar from './ProgressBar'
import VolumeControl from './VolumeControl'
import PlayerSettingsPanel from './PlayerSettingsPanel'

interface Props {
  state: PlayerState
  playerRef: RefObject<QueturePlayerHandle | null>
  containerRef: RefObject<HTMLDivElement | null>
  skipPolicy: 'always' | 'after_first_watch'
  speedPolicy: 'always' | 'after_first_watch'
  videoComponentEverCompleted: boolean
  playDisabled?: boolean
  onSeek?: (seconds: number) => void
  maxScrubSec?: number
  pausePoints?: ReadonlyArray<{
    time: number
    status: 'pending' | 'completed'
  }>
  appearance?: 'legacy' | 'v3'
}

function formatTime(sec: number): string {
  const s = Math.max(0, Math.floor(sec))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m}:${r.toString().padStart(2, '0')}`
}

export default function PlayerBar({
  state,
  playerRef,
  containerRef,
  skipPolicy,
  speedPolicy,
  videoComponentEverCompleted,
  playDisabled = false,
  onSeek,
  maxScrubSec,
  pausePoints,
  appearance = 'legacy',
}: Props) {
  const { t } = useTranslation('learner')
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  const canSeek = skipPolicy === 'always' || videoComponentEverCompleted
  const currentPercent = state.durationSec > 0 ? (state.currentSec / state.durationSec) * 100 : 0

  const handleSeekBlocked = useCallback(() => {
    toast.error(t('video.skipLockedFirstWatch') as string)
  }, [t])

  const handleFullscreen = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {})
    } else {
      el.requestFullscreen().catch(() => {})
    }
  }, [containerRef])

  return (
    <div
      data-appearance={appearance}
      className={
        appearance === 'v3'
          ? 'relative flex h-14 min-w-0 flex-1 items-center gap-4 rounded-xl bg-lc-bg-primary py-2 pl-3 pr-4 shadow-[0_4px_8px_rgba(0,0,0,0.05)]'
          : 'relative flex h-[72px] items-center gap-4 rounded-[16px] bg-white px-6 shadow-[0px_8px_16px_0px_rgba(0,0,0,0.2)]'
      }
    >
      <button
        type="button"
        data-action="toggle-play"
        onClick={state.togglePlay}
        disabled={playDisabled}
        className={
          appearance === 'v3'
            ? 'flex size-10 shrink-0 items-center justify-center text-lc-key-500 hover:text-lc-key-600 disabled:cursor-not-allowed disabled:text-lc-text-disabled'
            : 'flex size-10 shrink-0 items-center justify-center rounded-full bg-lc-lightblue-900 text-white hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:brightness-100'
        }
        aria-label={state.isPlaying ? '일시정지' : '재생'}
      >
        {state.isPlaying ? (
          <Pause
            className={appearance === 'v3' ? 'size-6' : 'size-5'}
            fill={appearance === 'v3' ? 'currentColor' : 'none'}
            strokeWidth={appearance === 'v3' ? 0 : 2}
            aria-hidden
          />
        ) : (
          <Play
            className={appearance === 'v3' ? 'size-6' : 'size-5 ml-0.5'}
            fill={appearance === 'v3' ? 'currentColor' : 'none'}
            strokeWidth={appearance === 'v3' ? 0 : 2}
            aria-hidden
          />
        )}
      </button>

      <ProgressBar
        currentPercent={currentPercent}
        durationSec={state.durationSec}
        canSeek={canSeek}
        getMaxProgressSec={() => playerRef.current?.getMaxProgressSec() ?? 0}
        onSeek={onSeek ?? state.seek}
        onSeekBlocked={handleSeekBlocked}
        maxScrubSec={maxScrubSec}
        pausePoints={pausePoints}
        appearance={appearance}
      />

      <div
        className={
          appearance === 'v3'
            ? 'hidden items-center gap-1 whitespace-nowrap tabular-nums text-lc-v3-body-1 font-medium sm:flex'
            : 'flex items-center gap-1 whitespace-nowrap tabular-nums text-[18px] font-medium tracking-[-0.6px]'
        }
      >
        <span className={appearance === 'v3' ? 'text-lc-key-500' : 'text-lc-lightblue-900'}>
          {formatTime(state.currentSec)}
        </span>
        <span className={appearance === 'v3' ? 'text-lc-v3-gray-400' : 'text-lc-gray-500'}>
          / {formatTime(state.durationSec)}
        </span>
      </div>

      <div className={appearance === 'v3' ? 'flex shrink-0 items-center gap-4' : 'contents'}>
        <VolumeControl
          volume={state.volume}
          isMuted={state.isMuted}
          onToggleMute={state.toggleMute}
          onChangeVolume={state.setVolumeValue}
          compact={appearance === 'v3'}
        />

        <div className="relative">
          <button
            type="button"
            onClick={() => setSettingsOpen((v) => !v)}
            className={[
              'flex items-center justify-center',
              appearance === 'v3'
                ? 'size-6 hover:bg-lc-v3-gray-50'
                : 'size-10 rounded-full hover:bg-lc-gray-100',
              settingsOpen
                ? appearance === 'v3' ? 'text-lc-key-500' : 'text-lc-lightblue-900'
                : appearance === 'v3' ? 'text-lc-v3-gray-600' : 'text-lc-gray-700',
            ].join(' ')}
            aria-label={t('video.speed') as string}
            aria-expanded={settingsOpen}
          >
            <Settings className="size-6" />
          </button>

          <PlayerSettingsPanel
            open={settingsOpen}
            playbackRate={state.playbackRate}
            speedPolicy={speedPolicy}
            videoComponentEverCompleted={videoComponentEverCompleted}
            onClose={() => setSettingsOpen(false)}
            onSelectRate={(rate) => {
              state.setRate(rate)
            }}
          />
        </div>

        <button
          type="button"
          onClick={handleFullscreen}
          className={
            appearance === 'v3'
              ? 'flex size-6 items-center justify-center text-lc-v3-gray-600 hover:text-lc-v3-gray-800'
              : 'flex size-10 items-center justify-center rounded-full text-lc-gray-700 hover:bg-lc-gray-100'
          }
          aria-label={isFullscreen ? '전체화면 해제' : '전체화면'}
        >
          {isFullscreen ? <Minimize className="size-6" /> : <Maximize className="size-6" />}
        </button>
      </div>
    </div>
  )
}
